var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { noop, clone } from '@ally/utilitarian';
import Channel from './Channel';
import FuncMap from './FuncMap';
import { HookTypes, } from '../types';
import { prefetch, getAppDiv, findMatchingApp, getAppContainer, logsUnmountError, listenForHistoryChanges, } from '../utils';
var isSameApp = function (x, y) { return (x === null || x === void 0 ? void 0 : x.id) === (y === null || y === void 0 ? void 0 : y.id); };
var isSamePath = function (x, y) { return x === y; };
/**
 * Manages the rendering of remote apps based on the window location.
 * Given some AppSchema, will watch for history events and `mount` and `unmount`
 * applications.
 *
 * @usage
 * const federator = new Federator({ ... })
 *
 * federator.setApps({
 *   appA: {
 *     match: ['/foo', '/bar']
 *     source: 'https://my-bundles/a.js',
 *   },
 *   appB: {
 *     match: ['/baz']
 *     source: 'https://my-bundles/b.js',
 *   },
 *   ...,
 * })
 *
 * federator.setHooks({
 *   onMount: [someFuncA, someFuncB],
 *   onMounted: someFuncC,
 *   ...,
 * })
 *
 * // Listens for route changes, invoking the given hooks and
 * // fetching/mounting/unmounting the apps provided based on the current route.
 * federator.listen()
 *
 * // Send a message to the currently mounted app.
 * federator.channel.send(message)
 *
 * // Stops listening for route change events
 * federator.unlisten()
 */
var Federator = /** @class */ (function () {
    function Federator(_a) {
        var _this = this;
        var hooks = _a.hooks, options = __rest(_a, ["hooks"]);
        this.meta = {};
        this.target = null;
        this.unlistener = noop;
        this.isListening = false;
        /**
         * A reference to the latest state update.
         * Public (mostly) for testing purposes.
         */
        this.pending = Promise.resolve();
        /**
         * A set of hooks that the end-user can set/update.
         * These are called throughout various times when apps are mounted/unmounted.
         */
        this.hooks = new FuncMap();
        /**
         * A channel to communicate with the currently mounted application.
         * This can be used to send messages to the mounted app from a parent app to,
         * for example, send some updated services (or something).
         */
        this.channel = new Channel();
        /**
         * Keeps track of the current window location and currently mounted app.
         * Used by Federator#listener below.
         */
        this.state = {
            mounted: null,
            pathname: null,
        };
        /**
         * Sets the list of routes that are federated.
         * This can be updated at runtime, but any new value will overwrite the old.
         * If prefetching is enabled, this will also prefetch the apps provided in
         * the schema if they haven't already been.
         */
        this.setApps = function (schema) {
            _this.config.apps = schema;
            _this.log.trace({ message: ['Apps updated:', _this.config.apps] });
            prefetch(_this.config);
            Object.keys(schema).forEach(function (k) {
                _this.meta[k] = _this.meta[k] || {
                    mounts: 0,
                    unmounts: 0,
                    isMounted: false,
                };
            });
            if (_this.target && _this.isListening) {
                _this.state.pathname = null;
                _this.listen(_this.target);
            }
        };
        /**
         * Updates the element to mount applications to.
         * If changed dynamically, the user is responsible for cleaning up an
         * extraneous elements in the old target.
         */
        this.setMountTarget = function (target) {
            _this.target = target;
        };
        /**
         * Starts listening to the native popstate and custom pushState/replaceState
         * events. Will cleanup any old event handlers before registering anymore
         * listeners.
         */
        this.listen = function (target) {
            _this.setMountTarget(target);
            _this.unlisten();
            _this.unlistener = listenForHistoryChanges(_this.listener);
            _this.isListening = true;
            _this.log.trace({ message: ['Federator listening:', _this.apps] });
            _this.listener();
        };
        /**
         * Mounts an application.
         *
         * This should not be called without unmounting an app first if one has
         * already been mounted. Invoke the app's `setup` method on first mount, then
         * it's `mount` method each time the app is mounted thereafter.
         *
         * This will create a new Channel instance for communicating with the app
         * through Federator#channel. Any previously bound channel will be unbound by
         * this action and GC'd if no other references remain.
         */
        this.mount = function (app) { return __awaiter(_this, void 0, void 0, function () {
            var _a, hooks, target, modules, meta, module, element, channel;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this, hooks = _a.hooks, target = _a.target, modules = _a.modules;
                        if (!(target instanceof HTMLDivElement)) {
                            throw new Error('Attempt to mount app without a valid target.');
                        }
                        hooks.call(HookTypes.onMount, app);
                        meta = this.meta[app.id];
                        return [4 /*yield*/, modules.get(app)];
                    case 1:
                        module = _b.sent();
                        if (!!meta.mounts) return [3 /*break*/, 3];
                        return [4 /*yield*/, (module.exports.setup || noop)()];
                    case 2:
                        _b.sent();
                        _b.label = 3;
                    case 3:
                        meta.mounts += 1;
                        meta.isMounted = true;
                        element = getAppContainer(app);
                        target.appendChild(element);
                        channel = new Channel(this.channel);
                        this.channel.bind(channel);
                        return [4 /*yield*/, (module.exports.mount || noop)(element, channel)];
                    case 4:
                        _b.sent();
                        hooks.call(HookTypes.onMounted, app);
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Unmounts an application.
         * This will unbind the app's channel and invoke it's `unmount` method which
         * should free any resources, then finally remove it from the DOM.
         */
        this.unmount = function (app) { return __awaiter(_this, void 0, void 0, function () {
            var _a, hooks, channel, modules, meta, target, module;
            var _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _a = this, hooks = _a.hooks, channel = _a.channel, modules = _a.modules;
                        meta = this.meta[app.id];
                        target = getAppDiv(app);
                        return [4 /*yield*/, modules.get(app)];
                    case 1:
                        module = _c.sent();
                        _c.label = 2;
                    case 2:
                        _c.trys.push([2, , 5, 6]);
                        channel.unbind();
                        hooks.call(HookTypes.onUnmount, app);
                        meta.unmounts += 1;
                        meta.isMounted = false;
                        if (!target) return [3 /*break*/, 4];
                        return [4 /*yield*/, (module.exports.unmount || noop)(target)];
                    case 3:
                        _c.sent();
                        _c.label = 4;
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        (_b = target === null || target === void 0 ? void 0 : target.parentElement) === null || _b === void 0 ? void 0 : _b.removeChild(target);
                        hooks.call(HookTypes.onUnmounted, app);
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        }); };
        /**
         * Used as an event callback for each of the history events: popstate,
         * pushState, and replaceState. This will inspect the current mount `state`
         * and determine if an app switch is necessary based on the previous and
         * current location.
         *
         * IMPORTANT: We have to chain together state changes to ensure that apps are
         * mounted/unmounted in order and hooks are called in the correct order.
         */
        this.listener = function (event) {
            var _a, _b;
            var pathname = window.location.pathname;
            (_a = event === null || event === void 0 ? void 0 : event.preventDefault) === null || _a === void 0 ? void 0 : _a.call(event);
            (_b = event === null || event === void 0 ? void 0 : event.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(event);
            if (isSamePath(_this.state.pathname, pathname))
                return;
            _this.log.debug({
                message: "[LOCATION EVENT] " + _this.state.pathname + " => " + pathname,
            });
            _this.state.pathname = pathname;
            _this.pending = _this.pending
                .then(function () { return _this.handleRouteTransition(pathname); })
                .catch(function (e) { return _this.handleRouteChangeError(e); })
                .then(function () {
                return _this.hooks.call(HookTypes.onRouteChangeEnd, _this.state.mounted);
            });
        };
        this.config = options;
        this.setHooks(hooks);
        this.setApps(options.apps);
    }
    Object.defineProperty(Federator.prototype, "log", {
        get: function () {
            return this.config.log;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Federator.prototype, "apps", {
        get: function () {
            return __assign({}, this.config.apps);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Federator.prototype, "modules", {
        get: function () {
            return this.config.modules;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Federator.prototype, "mounted", {
        get: function () {
            var _a, _b;
            return (_b = (_a = this.state.mounted) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Federator.prototype, "stats", {
        get: function () {
            return clone({
                meta: this.meta,
                state: this.state,
            });
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Exposes a function for setting hooks on Federator.
     */
    Federator.prototype.setHooks = function (hooks) {
        this.hooks.register(hooks);
        this.log.trace({ message: 'Registered new hooks...' });
    };
    /**
     * Resets this federator instance to its initial state.
     * All state (except stats) will be lost.
     *
     * In order to "re-initialize" it, the user will have to call the `setHooks`,
     * and `listen` methods again.
     */
    Federator.prototype.teardown = function () {
        this.unlisten();
        this.hooks.clear();
        this.target = null;
        this.state.mounted = null;
        this.state.pathname = null;
    };
    /**
     * Stops listening for route change events.
     */
    Federator.prototype.unlisten = function () {
        this.isListening = false;
        this.unlistener();
    };
    /**
     * Invoked by Federator#listener when switching between apps.
     * In the event an app fails to unmount, we can still attempt to mount the
     * next one. This may lead to memory leakage, but is better than the white
     * screen of death.
     */
    Federator.prototype.handleRouteTransition = function (pathname) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, log, apps, state, prev, next, isSame;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this, log = _a.log, apps = _a.apps, state = _a.state;
                        prev = this.state.mounted;
                        next = findMatchingApp(apps, pathname);
                        isSame = isSameApp(prev, next);
                        this.hooks.call(HookTypes.onRouteChangeStart, isSame);
                        if (isSame)
                            return [2 /*return*/];
                        state.mounted = next;
                        if (!prev) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.unmount(prev).catch(logsUnmountError(log, prev))];
                    case 1:
                        _b.sent();
                        _b.label = 2;
                    case 2:
                        if (!this.isListening) {
                            state.mounted = null;
                            log.warn({ message: 'Remote mount aborted, listening was cancelled.' });
                            return [2 /*return*/];
                        }
                        if (!next) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.mount(next)];
                    case 3:
                        _b.sent();
                        _b.label = 4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Handles when an error is thrown during a route transition.
     * We're setting the mount state to null, since we can't verify that the app
     * is actually mounted.
     */
    Federator.prototype.handleRouteChangeError = function (e) {
        this.hooks.call(HookTypes.onRouteChangeError, {
            error: e,
            remoteId: this.state.mounted != null ? this.state.mounted.id : '',
        });
        this.state.mounted = null;
    };
    return Federator;
}());
export default Federator;
//# sourceMappingURL=Federator.js.map